import { getAdminAnalytics } from "core/Services/AdminServices";
import { iAnalytic } from "core/kangarooTypes/iAnalytic";
import {
  ChevronDown,
  ShoppingCart,
  Truck,
  Users,
  BarChart3,
} from "lucide-react";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const AdminDashboard = () => {
  const { t } = useTranslation();
  const [orderData, setOrderData] = useState<{ name: string; value: number }[]>(
    []
  );
  const [feeData, setFeeData] = useState<{ name: string; value: number }[]>([]);
  const [balanceData, setBalanceData] = useState<
    { name: string; value: number }[]
  >([]);
  const [payoutData, setPayoutData] = useState<
    { name: string; value: number }[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [analytic, setAnalytic] = useState<iAnalytic>();
  const [dateFilter, setDateFilter] = useState<string>("7");

  const formatChartData = (data: any[], key: string) => {
    return data.map((item) => ({
      name: new Date(item.date || item.datee).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      }),
      value: parseFloat(item[key]),
    }));
  };

  const getDateRange = (days: string) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - parseInt(days));

    const formatDate = (date: Date) => {
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    return {
      from: formatDate(start),
      to: formatDate(end),
    };
  };

  // Function to check if there's meaningful data
  const hasData = () => {
    if (!analytic) return false;

    // Check if any of the widget counts are greater than 0
    const hasWidgetData =
      (analytic.widgets?.orders?.count || 0) > 0 ||
      (analytic.widgets?.merchants?.count || 0) > 0 ||
      (analytic.widgets?.driver?.count || 0) > 0;

    // Check if any chart data has content with actual values
    const hasChartData =
      (orderData.length > 0 && orderData.some((item) => item.value > 0)) ||
      (feeData.length > 0 && feeData.some((item) => item.value > 0)) ||
      (balanceData.length > 0 && balanceData.some((item) => item.value > 0)) ||
      (payoutData.length > 0 && payoutData.some((item) => item.value > 0));

    // Show dashboard if we have widget data OR meaningful chart data
    return hasWidgetData || hasChartData;
  };

  // Function to check if a specific chart has data
  const hasChartData = (data: { name: string; value: number }[]) => {
    return data.length > 0 && data.some((item) => item.value > 0);
  };

  const getData = async () => {
    try {
      setIsLoading(true);
      const { from, to } = getDateRange(dateFilter);
      const response = await getAdminAnalytics(from, to);

      setAnalytic(response);

      // Format chart data
      const formattedOrderData = formatChartData(
        response.charts.orders,
        "total_orders"
      );
      const formattedFeeData = formatChartData(
        response.charts.fees,
        "delevery_fees"
      );
      const formattedBalanceData = formatChartData(
        response.charts.merchant_balance,
        "delevery_fees"
      );
      const formattedPayoutData = formatChartData(
        response.charts.payment,
        "amount"
      );

      setOrderData(formattedOrderData);
      setFeeData(formattedFeeData);
      setBalanceData(formattedBalanceData);
      setPayoutData(formattedPayoutData);
    } catch (error) {
      toast.error("Failed to load analytics data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [dateFilter]);

  // No Data Component
  const NoDataAvailable = () => (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="text-center max-w-md">
        <div className="mx-auto mb-6 p-4 bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center">
          <BarChart3 className="h-10 w-10 text-gray-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-3">
          {t("adminDashboard.noDataAvailable.title")}
        </h3>
        <p className="text-gray-600 mb-6 leading-relaxed">
          {t("adminDashboard.noDataAvailable.message")}
        </p>
        <div className="text-sm text-gray-500">
          {t("adminDashboard.noDataAvailable.selectDifferentRange")}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col p-4 sm:p-6 max-w-7xl mx-auto">
      {isLoading ? (
        <div>
          <Skeleton height={40} width={200} className="mb-6" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <Skeleton height={120} className="rounded-lg" />
            <Skeleton height={120} className="rounded-lg" />
            <Skeleton height={120} className="rounded-lg" />
          </div>
          <Skeleton height={40} width={150} className="mb-4" />
          <Skeleton height={300} className="rounded-lg" />
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row justify-between items-center mb-6 sm:mb-8">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-0">
              {t("adminDashboard.analyticsOverview")}
            </h1>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
              <div className="relative w-full sm:w-auto">
                <select
                  className="w-full appearance-none bg-white border border-gray-300 px-4 py-2 pr-8 rounded-md focus:outline-none"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <option value="7">
                    {t("adminDashboard.dateFilters.last7Days")}
                  </option>
                  <option value="30">
                    {t("adminDashboard.dateFilters.last30Days")}
                  </option>
                  <option value="90">
                    {t("adminDashboard.dateFilters.last90Days")}
                  </option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <ChevronDown size={20} />
                </div>
              </div>
            </div>
          </div>

          {/* Show No Data message when there's no meaningful data */}
          {!hasData() ? (
            <NoDataAvailable />
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
                {/* Total Orders */}
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex items-center mb-2">
                    <h2 className="text-gray-600 text-sm font-medium flex-grow">
                      {t("adminDashboard.stats.totalOrders")}
                    </h2>
                    <div className="ml-2 p-1 rounded-full bg-gray-100">
                      <ShoppingCart className="h-4 w-4 text-gray-500" />
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-2xl sm:text-3xl font-bold text-gray-800">
                      {analytic?.widgets.orders.count}
                    </span>
                    <span className="text-xs sm:text-sm text-green-600 font-medium">
                      {analytic?.widgets.orders.percentage_change}%{" "}
                      {t("adminDashboard.stats.fromLastMonth")}
                    </span>
                  </div>
                </div>

                {/* Active Merchants */}
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex items-center mb-2">
                    <h2 className="text-gray-600 text-sm font-medium flex-grow">
                      {t("adminDashboard.stats.activeMerchants")}
                    </h2>
                    <div className="ml-2 p-1 rounded-full bg-gray-100">
                      <Users className="h-4 w-4 text-gray-500" />
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-2xl sm:text-3xl font-bold text-gray-800">
                      {analytic?.widgets.merchants.count}
                    </span>
                    <span className="text-xs sm:text-sm text-green-600 font-medium">
                      {analytic?.widgets.merchants.percentage_change}%{" "}
                      {t("adminDashboard.stats.fromLastMonth")}
                    </span>
                  </div>
                </div>

                {/* Active Drivers */}
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-100 col-span-full sm:col-span-1 lg:col-span-1">
                  <div className="flex items-center mb-2">
                    <h2 className="text-gray-600 text-sm font-medium flex-grow">
                      {t("adminDashboard.stats.activeDrivers")}
                    </h2>
                    <div className="ml-2 p-1 rounded-full bg-gray-100">
                      <Truck className="h-4 w-4 text-gray-500" />
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-2xl sm:text-3xl font-bold text-gray-800">
                      {analytic?.widgets.driver.count}
                    </span>
                    <span className="text-xs sm:text-sm text-green-600 font-medium">
                      {analytic?.widgets.driver.percentage_change}%{" "}
                      {t("adminDashboard.stats.fromLastMonth")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Charts Section */}
              <div className="space-y-6 sm:space-y-8">
                {/* Orders Overview */}
                <div>
                  <h2 className="text-lg sm:text-xl font-medium text-gray-800 mb-4">
                    {t("adminDashboard.charts.ordersOverview")}
                  </h2>
                  <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm">
                    {hasChartData(orderData) ? (
                      <div className="h-48 sm:h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={orderData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Line
                              type="monotone"
                              dataKey="value"
                              stroke="#4F46E5"
                              strokeWidth={2}
                              dot={{ r: 4 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-48 sm:h-64 flex items-center justify-center">
                        <div className="text-center">
                          <BarChart3 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                          <p className="text-gray-500">
                            {t("adminDashboard.noDataAvailable.title")}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Delivery Fees */}
                <div>
                  <h2 className="text-lg sm:text-xl font-medium text-gray-800 mb-4">
                    {t("adminDashboard.charts.deliveryFees")}
                  </h2>
                  <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm">
                    {hasChartData(feeData) ? (
                      <div className="h-48 sm:h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={feeData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Line
                              type="monotone"
                              dataKey="value"
                              stroke="#10B981"
                              strokeWidth={2}
                              dot={{ r: 4 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-48 sm:h-64 flex items-center justify-center">
                        <div className="text-center">
                          <BarChart3 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                          <p className="text-gray-500">
                            {t("adminDashboard.noDataAvailable.title")}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payments */}
                <div>
                  <h2 className="text-lg sm:text-xl font-medium text-gray-800 mb-4">
                    {t("adminDashboard.charts.payments")}
                  </h2>
                  <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm">
                    {hasChartData(payoutData) ? (
                      <div className="h-48 sm:h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={payoutData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Line
                              type="monotone"
                              dataKey="value"
                              stroke="#EF4444"
                              strokeWidth={2}
                              dot={{ r: 4 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-48 sm:h-64 flex items-center justify-center">
                        <div className="text-center">
                          <BarChart3 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                          <p className="text-gray-500">
                            {t("adminDashboard.noDataAvailable.title")}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
